import clsx from "clsx";
import { useNow } from "../hooks/useNow";

interface RefreshIndicatorProps {
  /** Last successful data update timestamp (ms epoch). From TanStack Query's dataUpdatedAt. */
  dataUpdatedAt: number;
  /** Whether a fetch is currently in flight (TanStack Query's isFetching). */
  isFetching: boolean;
  /** Refetch interval in ms (must match useStatus' refetchInterval). */
  refetchIntervalMs: number;
  /** Manual refresh callback (typically `() => refetch()`). */
  onRefresh: () => void;
}

/**
 * Refresh indicator — top-right corner.
 *
 * Shows:
 *   - elapsed seconds since last successful update ("12초 전 갱신")
 *   - countdown to next auto-refresh ("23초 후 자동 갱신")
 *   - in-flight spinner when isFetching is true
 *   - manual "지금 새로고침" button
 *
 * The 1-second tick comes from the shared `useNow()` hook — no local
 * setInterval. TanStack Query owns the actual data refetch cadence
 * (refetchInterval); this component is read-only with respect to fetching.
 */
export default function RefreshIndicator({
  dataUpdatedAt,
  isFetching,
  refetchIntervalMs,
  onRefresh,
}: RefreshIndicatorProps) {
  const now = useNow();

  const elapsedMs = dataUpdatedAt > 0 ? now - dataUpdatedAt : 0;
  const elapsedSec = Math.max(0, Math.floor(elapsedMs / 1000));
  const remainingSec = Math.max(
    0,
    Math.ceil((refetchIntervalMs - elapsedMs) / 1000),
  );

  return (
    <div className="flex items-center gap-3">
      <div className="flex flex-col items-end text-[11px] leading-tight text-slate-500 dark:text-slate-400 sm:text-xs">
        <span className="tabular-nums">
          {dataUpdatedAt > 0 ? `${elapsedSec}초 전 갱신` : "갱신 대기"}
        </span>
        <span className="tabular-nums">
          {isFetching ? "갱신 중…" : `${remainingSec}초 후 자동 갱신`}
        </span>
      </div>

      <button
        type="button"
        onClick={onRefresh}
        disabled={isFetching}
        aria-label="지금 새로고침"
        className={clsx(
          // 모바일은 44px(h-11) 확보, 데스크톱은 기존 36px 유지 — 헤더가 두꺼워지지 않게.
          "inline-flex h-11 w-11 items-center justify-center rounded-full border transition sm:h-9 sm:w-9",
          "border-surface-border bg-surface text-slate-600 hover:bg-slate-100",
          "dark:border-surface-darkBorder dark:bg-surface-darkSubtle dark:text-slate-300 dark:hover:bg-slate-800",
          "disabled:opacity-50 disabled:cursor-not-allowed",
        )}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          className={clsx("h-4 w-4", isFetching && "animate-spin")}
          aria-hidden="true"
        >
          <path d="M21 12a9 9 0 1 1-3-6.7" />
          <path d="M21 3v6h-6" />
        </svg>
      </button>
    </div>
  );
}
